import { db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';

export interface PromptBoxInstructions {
  inConversation: string;
  newConversation: string;
  helpmestartPostscriptIntro: string;
  customreviewPostscriptIntro: string;
}

export class TaglineService {
  /**
   * Fetch all taglines from Firestore
   * Taglines are now stored as an array in system/config document
   */
  static async getTaglines(): Promise<string[]> {
    try {
      console.log('🔍 Fetching taglines from Firestore...');

      const configRef = doc(db, 'system', 'config');
      const configSnap = await getDoc(configRef);

      if (!configSnap.exists()) {
        console.warn('⚠️ System config document not found in Firestore');
        return [];
      }

      const data = configSnap.data();
      const taglines = data.taglines as string[] || [];

      if (taglines.length === 0) {
        console.warn('⚠️ No taglines found in system config');
        return [];
      }

      console.log(`📊 Retrieved ${taglines.length} taglines from Firestore`);
      return taglines;
    } catch (error) {
      console.error('❌ Error fetching taglines from Firestore:', error);
      throw error;
    }
  }

  /**
   * Fetch prompt box instruction text from Firestore
   * Falls back to default values if not found
   */
  static async getPromptBoxInstructions(): Promise<PromptBoxInstructions> {
    try {
      console.log('🔍 Fetching prompt box instructions from Firestore...');

      const configRef = doc(db, 'system', 'config');
      const configSnap = await getDoc(configRef);

      const defaultInstructions: PromptBoxInstructions = {
        inConversation: 'add context to this conversation',
        newConversation: 'conversations better than with any lone AI chatbot start here...',
        helpmestartPostscriptIntro: 'postscript: the user-generated language above was authored in context of the following:',
        customreviewPostscriptIntro: 'postscript. generate an excellent response to the above prompt as defined by the following rubric:'
      };

      if (!configSnap.exists()) {
        console.warn('⚠️ System config document not found, using defaults');
        return defaultInstructions;
      }

      const data = configSnap.data();
      const instructions: PromptBoxInstructions = {
        inConversation: data.promptBoxInConversationInstruction || defaultInstructions.inConversation,
        newConversation: data.promptBoxNewConversationInstruction || defaultInstructions.newConversation,
        helpmestartPostscriptIntro: data.helpmestartPromptBoxPostscriptIntro || defaultInstructions.helpmestartPostscriptIntro,
        customreviewPostscriptIntro: data.customreviewPromptBoxPostscriptIntro || defaultInstructions.customreviewPostscriptIntro
      };

      console.log('📊 Retrieved prompt box instructions:', instructions);
      return instructions;
    } catch (error) {
      console.error('❌ Error fetching prompt box instructions from Firestore:', error);
      // Return defaults on error
      return {
        inConversation: 'add context to this conversation',
        newConversation: 'conversations better than with any lone AI chatbot start here...',
        helpmestartPostscriptIntro: 'postscript: the user-generated language above was authored in context of the following:',
        customreviewPostscriptIntro: 'postscript. generate an excellent response to the above prompt as defined by the following rubric:'
      };
    }
  }

  /**
   * Fetch welcome messages from Firestore
   * Welcomes are stored as an array in system/config document
   */
  static async getWelcomes(): Promise<string[]> {
    try {
      console.log('🔍 Fetching welcomes from Firestore...');

      const configRef = doc(db, 'system', 'config');
      const configSnap = await getDoc(configRef);

      if (!configSnap.exists()) {
        console.warn('⚠️ System config document not found in Firestore');
        return ['better than any lone ai chatbot: welcome to ionoff'];
      }

      const data = configSnap.data();
      const welcomes = data.welcomes as string[] || [];

      if (welcomes.length === 0) {
        console.warn('⚠️ No welcomes found in system config, using default');
        return ['better than any lone ai chatbot: welcome to ionoff'];
      }

      console.log(`📊 Retrieved ${welcomes.length} welcomes from Firestore`);
      return welcomes;
    } catch (error) {
      console.error('❌ Error fetching welcomes from Firestore:', error);
      return ['better than any lone ai chatbot: welcome to ionoff'];
    }
  }

  /**
   * Fetch icebreaker prompts from Firestore
   * Icebreakers are stored as an array in system/config document
   */
  static async getIcebreakers(): Promise<string[]> {
    const defaultIcebreakers = [
      "what's a question you wish people asked you more often?",
      "what's your top \"more\" priority? time, money, health, relationships, learning, or creativity? why?"
    ];
    
    try {
      console.log('🔍 Fetching icebreakers from Firestore...');

      const configRef = doc(db, 'system', 'config');
      const configSnap = await getDoc(configRef);

      if (!configSnap.exists()) {
        console.warn('⚠️ System config document not found in Firestore');
        return defaultIcebreakers;
      }

      const data = configSnap.data();
      const icebreakers = data.icebreakers as string[] || [];

      if (icebreakers.length === 0) {
        console.warn('⚠️ No icebreakers found in system config, using default');
        return defaultIcebreakers;
      }

      console.log(`📊 Retrieved ${icebreakers.length} icebreakers from Firestore`);
      return icebreakers;
    } catch (error) {
      console.error('❌ Error fetching icebreakers from Firestore:', error);
      return defaultIcebreakers;
    }
  }
}
















